import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { AiMutationKeys } from "#lib/AiQueryKeys.ts";
import { isApiError } from "#lib/http/ApiError.ts";
import { normalizeFileMimeType, supportedMimeTypes } from "#lib/utils/fileMimeType.ts";
import { useMutation } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";
import { toast } from "react-toastify";
import { blob, maxSize, mimeType, parse, pipe, ValiError } from "valibot";

const MAX_FILE_SIZE_MB = 32;

interface MutationPayload {
    file: File;
    abortController: AbortController;
    conversationId: string;
}

interface Options {
    onSuccess: (fileId: string, fileUploadUrl: string, file: File) => void;
}

export const useGenerateConversationFileUploadUrlMutation = (setFiles: Dispatch<SetStateAction<FileModel[]>>, { onSuccess }: Options) => {
    const { generateConversationFileUploadUrl } = useAiApiClient();

    const { tShared } = useSharedTranslation();

    const fileSchema = pipe(
        blob(),
        mimeType(supportedMimeTypes, tShared("fileCard.errors.fileTypeInvalid")),
        maxSize(1024 * 1024 * MAX_FILE_SIZE_MB, tShared("fileCard.errors.sizeInvalid", { maxSize: MAX_FILE_SIZE_MB }))
    );

    return useMutation({
        mutationKey: AiMutationKeys.GenerateFileUploadUrl(),

        onMutate: async ({ file, abortController }: MutationPayload) => {
            const fileId = Math.random().toString();

            setFiles(prevFiles => {
                const newFile: FileModel = {
                    id: fileId,
                    fileName: file.name,
                    fileUploadStatus: "Uploading",
                    abortController
                };

                return [...prevFiles, newFile];
            });

            return { fileId };
        },
        mutationFn: async ({ file, abortController, conversationId }: MutationPayload) => {
            const normalizedFile = normalizeFileMimeType(file);

            parse(fileSchema, normalizedFile, { abortEarly: true });

            const data = await generateConversationFileUploadUrl(
                { fileName: normalizedFile.name, mimeType: normalizedFile.type, conversationId },
                abortController
            );

            return { ...data, normalizedFile };
        },
        onSuccess: (result, _, { fileId }) => {
            setFiles(prevFiles => {
                const updatedFiles = prevFiles.map<FileModel>(prevFile =>
                    prevFile.id === fileId ? { ...prevFile, id: result.fileId } : prevFile
                );

                return updatedFiles;
            });

            onSuccess(result.fileId, result.fileUploadUrl, result.normalizedFile);
        },
        onError: (error, _, context) => {
            // A 409 means the upload can't succeed as-is (e.g. a conflicting file), so retrying the
            // same file won't help — surface it as a toast and drop the file instead of leaving a
            // dead card in the list.
            if (isApiError(error) && error.status === 409) {
                setFiles(prevFiles => prevFiles.filter(prevFile => prevFile.id !== context?.fileId));
                toast.error(tShared("fileCard.errors.uploadFailed"));

                return;
            }

            let message = tShared("fileCard.errors.uploadFailed");

            if (error instanceof ValiError) {
                message = error.issues?.[0]?.message;
            }

            setFiles(prevFiles => {
                const updatedFiles = prevFiles.map<FileModel>(prevFile =>
                    prevFile.id === context?.fileId ? { ...prevFile, fileUploadStatus: "Failed", error: message } : prevFile
                );

                return updatedFiles;
            });
        }
    });
};
